import { ShieldCheck, Trash2 } from "lucide-react";

import type { TargetListItem } from "../../api/targets";
import { useIsMobile } from "../../lib/useMediaQuery";

interface TargetsListProps {
  items: TargetListItem[];
  selectedId: string | null;
  matchedPhone: string | null;
  onSelect: (t: TargetListItem) => void;
  onRequestDelete: (t: TargetListItem) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
}

export function TargetsList({
  items,
  selectedId,
  matchedPhone,
  onSelect,
  onRequestDelete,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: TargetsListProps) {
  const isMobile = useIsMobile();
  const allSelected = items.length > 0 && items.every((t) => selectedIds.has(t.target_id));
  return (
    <div
      style={{
        background: "var(--bg-card)",
        backdropFilter: "blur(var(--glass-blur))",
        border: "1px solid var(--glass-border)",
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        maxHeight: 620,
      }}
    >
      {!isMobile && <ListHeader allSelected={allSelected} onToggleSelectAll={onToggleSelectAll} />}
      <div style={{ overflow: "auto", flex: 1 }}>
        {items.map((t) => {
          const isSelected = t.target_id === selectedId;
          const isPhoneMatch =
            matchedPhone != null && t.primary_phone_e164 === matchedPhone;
          const checked = selectedIds.has(t.target_id);
          return isMobile ? (
            <ListCard
              key={t.target_id}
              target={t}
              selected={isSelected}
              phoneMatch={isPhoneMatch}
              checked={checked}
              onClick={() => onSelect(t)}
              onToggleSelect={() => onToggleSelect(t.target_id)}
              onRequestDelete={() => onRequestDelete(t)}
            />
          ) : (
            <ListRow
              key={t.target_id}
              target={t}
              selected={isSelected}
              phoneMatch={isPhoneMatch}
              checked={checked}
              onClick={() => onSelect(t)}
              onToggleSelect={() => onToggleSelect(t.target_id)}
              onRequestDelete={() => onRequestDelete(t)}
            />
          );
        })}
        {items.length === 0 && (
          <div
            style={{
              padding: "24px 14px",
              color: "var(--text-muted)",
              textAlign: "center",
              fontSize: 11,
            }}
          >
            No targets match the current query.
          </div>
        )}
      </div>
    </div>
  );
}

const GRID_COLUMNS = "28px 1.4fr 1.1fr 1.4fr 1fr 36px";

function ListHeader({
  allSelected,
  onToggleSelectAll,
}: {
  allSelected: boolean;
  onToggleSelectAll: () => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: GRID_COLUMNS,
        gap: 10,
        padding: "8px 14px",
        fontSize: 9.5,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        color: "var(--text-muted)",
        fontWeight: 700,
        background: "var(--bg-secondary)",
        borderBottom: "1px solid var(--border-subtle)",
        alignItems: "center",
      }}
    >
      <input
        type="checkbox"
        checked={allSelected}
        onChange={onToggleSelectAll}
        aria-label="Select all targets"
        style={{ cursor: "pointer" }}
      />
      <span>Name</span>
      <span>Phone</span>
      <span>Email</span>
      <span>Groups</span>
      <span />
    </div>
  );
}

interface ListRowProps {
  target: TargetListItem;
  selected: boolean;
  phoneMatch: boolean;
  checked: boolean;
  onClick: () => void;
  onToggleSelect: () => void;
  onRequestDelete: () => void;
}

function ListRow({
  target,
  selected,
  phoneMatch,
  checked,
  onClick,
  onToggleSelect,
  onRequestDelete,
}: ListRowProps) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_COLUMNS,
        gap: 10,
        padding: "9px 14px",
        borderBottom: "1px solid var(--border-subtle)",
        borderLeft: selected ? "3px solid var(--accent)" : "3px solid transparent",
        alignItems: "center",
        fontSize: 11,
        cursor: "pointer",
        background: selected ? "var(--accent-subtle)" : "transparent",
        transition: "background 0.1s",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onClick={(e) => e.stopPropagation()}
        onChange={onToggleSelect}
        aria-label={`Select ${target.full_name}`}
        style={{ cursor: "pointer" }}
      />

      <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
        {target.full_name}
      </span>

      <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-secondary)", fontSize: 10.5 }}>
        {phoneMatch ? (
          <span
            style={{
              background: "rgba(34,197,94,0.10)",
              color: "var(--success)",
              padding: "2px 6px",
              borderRadius: 4,
              border: "1px solid rgba(34,197,94,0.25)",
              display: "inline-block",
            }}
          >
            {target.primary_phone_e164}
          </span>
        ) : (
          target.primary_phone_e164 ?? "—"
        )}
      </span>

      <span
        style={{
          fontFamily: "var(--font-mono)",
          color: "var(--text-secondary)",
          fontSize: 10.5,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {target.primary_email ?? "—"}
      </span>

      <GroupChips target={target} />

      <button
        onClick={(e) => {
          e.stopPropagation();
          onRequestDelete();
        }}
        title={`Delete ${target.full_name}`}
        aria-label={`Delete ${target.full_name}`}
        style={{
          background: "transparent",
          border: "none",
          color: "var(--text-muted)",
          cursor: "pointer",
          padding: 4,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function ListCard({
  target,
  selected,
  phoneMatch,
  checked,
  onClick,
  onToggleSelect,
  onRequestDelete,
}: ListRowProps) {
  const roleLabel = [target.role, target.department].filter(Boolean).join(" · ") || "—";

  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "12px 14px",
        borderBottom: "1px solid var(--border-subtle)",
        borderLeft: selected ? "3px solid var(--accent)" : "3px solid transparent",
        cursor: "pointer",
        background: selected ? "var(--accent-subtle)" : "transparent",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={onToggleSelect}
            aria-label={`Select ${target.full_name}`}
            style={{ cursor: "pointer" }}
          />
          <span style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: 13 }}>
            {target.full_name}
          </span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRequestDelete();
          }}
          title={`Delete ${target.full_name}`}
          aria-label={`Delete ${target.full_name}`}
          style={{
            background: "transparent",
            border: "none",
            color: "var(--text-muted)",
            cursor: "pointer",
            padding: 4,
          }}
        >
          <Trash2 size={15} />
        </button>
      </div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-secondary)" }}>
        {phoneMatch ? (
          <span
            style={{
              background: "rgba(34,197,94,0.10)",
              color: "var(--success)",
              padding: "2px 6px",
              borderRadius: 4,
              border: "1px solid rgba(34,197,94,0.25)",
              display: "inline-block",
            }}
          >
            {target.primary_phone_e164}
          </span>
        ) : (
          target.primary_phone_e164 ?? "—"
        )}
      </div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {target.primary_email ?? "—"}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{roleLabel}</div>
      <GroupChips target={target} />
    </div>
  );
}

// Group-membership chips: up to 2 names then "+N". "—" when ungrouped.
function GroupChips({ target }: { target: TargetListItem }) {
  const names = target.group_names ?? [];
  const isTest = target.test_only;
  // A test-pool number with no group is NOT an orphan — show a distinct pill so
  // it isn't mistaken for a deletable orphan. "—" is reserved for true orphans.
  if (names.length === 0 && !isTest) {
    return <span style={{ color: "var(--text-muted)", fontSize: 10.5 }}>—</span>;
  }
  const shown = names.slice(0, 2);
  const extra = names.length - shown.length;
  return (
    <span style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
      {isTest && (
        <span
          title="Verified test-pool number — not a campaign target"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            background: "var(--accent-subtle)",
            border: "1px solid var(--accent)",
            borderRadius: 4,
            padding: "1px 6px",
            fontSize: 10,
            fontWeight: 600,
            color: "var(--accent)",
            whiteSpace: "nowrap",
          }}
        >
          <ShieldCheck size={10} /> Test pool
        </span>
      )}
      {shown.map((n) => (
        <span
          key={n}
          style={{
            background: "var(--bg-tertiary)",
            border: "1px solid var(--border-subtle)",
            borderRadius: 4,
            padding: "1px 6px",
            fontSize: 10,
            color: "var(--text-secondary)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 110,
          }}
        >
          {n}
        </span>
      ))}
      {extra > 0 && (
        <span style={{ fontSize: 10, color: "var(--text-muted)" }}>+{extra}</span>
      )}
    </span>
  );
}
